<script>
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import WishlistButton from '@maison/components/wishlist/WishlistButton.vue';
import LimitedSerialNumber from '@maison/components/common/LimitedSerialNumber';
import { PhxIcon } from '@phx-styleguides/rdu';

export default {
  components: {
    WishlistButton,
    PictureLazy,
    LimitedSerialNumber,
    PhxIcon,
  },

  props: {
    reference: {
      type: String,
      required: true,
    },
    reservationNumber: {
      type: String,
      required: true,
    },
    image: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    sapReference: {
      type: String,
      required: true,
    },
    errors: {
      type: Array,
      required: false,
      default: () => [],
    },
    price: {
      type: String,
      default: null,
    },
    taxIncluded: {
      type: Boolean,
      required: false,
      default: true,
    },
    prepaymentPrice: {
      type: String,
      required: false,
      default: null,
    },
    collectionName: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    selectablePieceNumbers: {
      type: Number,
      default: 0,
    },
    desiredPieceNumbers: {
      type: Array,
      default: () => [],
    },
    shouldDisplayAddToWishlist: {
      type: Boolean,
      default: false,
    },
    shouldDisplayRemoveFromCart: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: '',
    },
  },

  emits: ['removeFromCart', 'changeSerialNumber'],

  methods: {
    removeFromCart(reservationNumber) {
      this.$emit('removeFromCart', reservationNumber);
    },

    changeSerialNumber() {
      this.$emit('changeSerialNumber');
    },
  },
};
</script>

<template>
  <article :data-cy-reference="reference" :data-cy-reservation-number="reservationNumber">
    <div class="flex flex-row gap-2">
      <div class="relative w-3/12 md:w-2/6 text-center">
        <div class="picture checkout__cart-item-picture">
          <picture-lazy v-bind="image" />
        </div>
      </div>

      <div class="flex flex-col w-9/12 md:w-4/6">
        <div class="flex flex-row justify-between">
          <div class="py-3 pe-3">
            <h4 v-if="collectionName" class="h6">{{ collectionName }}</h4>
            <h3 class="h5">{{ title }}</h3>
            <div class="mt-1 text-xs text-grey-600" data-cy="checkout-cart-item-subtitle">
              {{ sapReference }}
              <template v-if="description">
                <br />
                {{ description }}
              </template>
            </div>
            <div v-if="size" class="text-xs italic text-grey-600">{{ size }}</div>

            <div
              v-for="error in errors"
              :key="`$error_{id}_${error}`"
              class="alert alert--badge mt-8 md:mt-6"
              data-cy="no-stock-alert-item"
            >
              <div class="alert__icon--badge">
                <phx-icon icon="info" />
              </div>
              {{ $t(`Checkout.product.${error}.tag`) }}
            </div>

            <div
              v-if="prepaymentPrice"
              class="alert alert--badge alert--light mt-8 md:mt-6"
              data-cy="checkout-cart-item-prepayment"
            >
              <div class="alert__icon--badge">
                <phx-icon icon="info" />
              </div>
              <span
                v-html="
                  $t('Checkout.product.prepaymentPrice', {
                    price: `<b>${prepaymentPrice}</b>`,
                  })
                "
              />
            </div>

            <div class="h6 mt-8 md:mt-6">{{ price }}</div>
            <div v-if="!taxIncluded" class="text-xs text-grey-600">{{ $t('Checkout.taxesIncluded') }}</div>
          </div>

          <div class="flex flex-col items-start text-end md:flex-row-reverse">
            <button
              v-if="shouldDisplayRemoveFromCart"
              type="button"
              class="button button--icon button--md"
              :aria-label="
                $t('Checkout.removeProduct', {
                  product: title,
                })
              "
              data-cy="remove-cart-item-button"
              @click="removeFromCart(reservationNumber)"
            >
              <phx-icon icon="close" class="ico--small" />
            </button>
            <wishlist-button
              v-if="shouldDisplayAddToWishlist"
              :reference="reference"
              icon-size="medium"
              button-size="md"
            />
          </div>
        </div>

        <limited-serial-number
          v-if="selectablePieceNumbers"
          :desired-piece-numbers="desiredPieceNumbers"
          class="hidden md:flex mt-2 pe-3"
          @change-serial-number="changeSerialNumber"
        />
      </div>
    </div>

    <limited-serial-number
      v-if="selectablePieceNumbers"
      :desired-piece-numbers="desiredPieceNumbers"
      class="mt-6 pb-3 md:hidden"
      @change-serial-number="changeSerialNumber"
    />
  </article>
</template>
